import { useCallback, useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { createPortal } from 'react-dom'
import { api } from '@pilot-hub/api-client'
import { Button, useBestaetigung, useModal } from '@pilot-hub/ui'
import { Share2, X, Copy, Download, Printer, Ban, RefreshCw } from 'lucide-react'

// Freigabelink für Externe (Leitstelle, Polizei): Livebild + Drohnenpositionen ohne Login, gültig bis Einsatzende.
// Nur Führung darf erzeugen/sperren (fuehrung-api /einsaetze/{id}/freigabelink). Der QR-Code entsteht lokal im Browser.

interface Protokoll { ereignis: string; zeit: string; ip: string | null; user_agent: string | null; nutzer: string | null }
interface LinkStatus {
  status: 'keiner' | 'aktiv' | 'gesperrt' | 'abgelaufen'
  einsatz_abgeschlossen: boolean
  token: string | null
  link: { id: number; erstellt_am: string; erstellt_von: string | null; gesperrt_am: string | null; abgelaufen_am: string | null; zuletzt_geoeffnet_am: string | null; aufrufe: number } | null
  protokoll: Protokoll[]
}

const zeit = (s: string | null | undefined) => (s ? new Date(s).toLocaleString('de-DE', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '–')
const EREIGNIS: Record<string, string> = { ERSTELLT: 'Link erzeugt', GEOEFFNET: 'Geöffnet', GESPERRT: 'Gesperrt', ABGELAUFEN: 'Abgelaufen (Einsatzende)' }

/** In die Zwischenablage kopieren; Fallback für ältere Browser/iPad (execCommand) bzw. Auswahl zum manuellen Kopieren. */
async function kopieren(text: string, feld: HTMLInputElement | null): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) { await navigator.clipboard.writeText(text); return true }
  } catch { /* weiter mit Fallback */ }
  if (feld) {
    feld.focus(); feld.select(); feld.setSelectionRange(0, text.length)
    try { if (document.execCommand('copy')) return true } catch { /* manuell */ }
  }
  return false
}

export function FreigabeLinkButton({ einsatzId, className = '', kompakt = false }: { einsatzId: string; className?: string; kompakt?: boolean }) {
  const [offen, setOffen] = useState(false)
  return (
    <>
      <Button variant="secondary" className={kompakt ? `h-11 w-11 min-h-0 !px-0 ${className}` : `min-h-[48px] ${className}`} disabled={!einsatzId} onClick={() => setOffen(true)}
        title="Link und QR-Code für Externe (z. B. Leitstelle, Polizei) erzeugen"
        aria-label={kompakt ? 'Freigabelink für Externe' : undefined}>
        <Share2 className={kompakt ? 'h-4 w-4' : 'mr-2 h-4 w-4'} />{!kompakt && 'Freigabelink für Externe'}
      </Button>
      {offen && <FreigabeLinkDialog einsatzId={einsatzId} onSchliessen={() => setOffen(false)} />}
    </>
  )
}

export function FreigabeLinkDialog({ einsatzId, onSchliessen }: { einsatzId: string; onSchliessen: () => void }) {
  const { bestaetigen, dialog } = useBestaetigung()
  const panel = useRef<HTMLDivElement>(null)
  useModal(true, panel, onSchliessen)
  const [st, setSt] = useState<LinkStatus | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const [qr, setQr] = useState<string | null>(null)
  const [hinweis, setHinweis] = useState<string | null>(null)
  const [protokollOffen, setProtokollOffen] = useState(false)
  const feld = useRef<HTMLInputElement>(null)
  const url = st?.token ? `${window.location.origin}/extern/${st.token}` : null

  const laden = useCallback(async () => {
    try { setSt((await api.get(`/einsaetze/${einsatzId}/freigabelink`)).data as LinkStatus); setFehler(null) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Status konnte nicht geladen werden') }
  }, [einsatzId])
  useEffect(() => { laden(); const t = window.setInterval(laden, 15000); return () => window.clearInterval(t) }, [laden])

  useEffect(() => {
    if (!url) { setQr(null); return }
    QRCode.toDataURL(url, { width: 640, margin: 2, errorCorrectionLevel: 'M' }).then(setQr).catch(() => setQr(null))
  }, [url])

  async function aktion(pfad: string, bestaetigung?: { titel: string; hinweis: string; bestaetigenText: string }) {
    if (bestaetigung && !(await bestaetigen({ ...bestaetigung, text: `Freigabelink zu Einsatz Nr. ${einsatzId}`, gefahr: true }))) return
    setLaeuft(true); setFehler(null)
    try { setSt((await api.post(`/einsaetze/${einsatzId}/freigabelink${pfad}`)).data as LinkStatus) }
    catch (e: any) { setFehler(e?.response?.data?.detail || 'Aktion fehlgeschlagen') }
    finally { setLaeuft(false) }
  }

  async function kopierenKlick() {
    if (!url) return
    const ok = await kopieren(url, feld.current)
    setHinweis(ok ? 'Link kopiert' : 'Link ist markiert – bitte lange drücken bzw. Strg+C zum Kopieren')
    window.setTimeout(() => setHinweis(null), 4000)
  }

  function herunterladen() {
    if (!qr) return
    const a = document.createElement('a'); a.href = qr; a.download = `freigabe-einsatz-${einsatzId}.png`; a.click()
  }

  function drucken() {
    if (!qr || !url) return
    const w = window.open('', '_blank', 'width=700,height=900')
    if (!w) { setFehler('Pop-up für das Drucken wurde blockiert – bitte den QR-Code herunterladen.'); return }
    w.document.write(`<!doctype html><html lang="de"><head><meta charset="utf-8"><title>Freigabe Einsatz ${einsatzId}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:30px}img{width:420px;height:420px}code{word-break:break-all;font-size:13px}</style></head>
      <body><h2>Live-Lage zum Einsatz Nr. ${einsatzId}</h2><p>QR-Code scannen: Livebild und Drohnenpositionen</p>
      <img src="${qr}" alt="QR-Code"><p><code></code></p><p style="font-size:13px">Gültig bis zum Einsatzende.</p></body></html>`)
    w.document.querySelector('code')!.textContent = url
    w.document.close(); w.focus(); setTimeout(() => w.print(), 300)
  }

  const aktiv = st?.status === 'aktiv'
  const statusText = !st ? 'Lade …'
    : st.status === 'aktiv' ? 'Aktiv' : st.status === 'gesperrt' ? 'Gesperrt' : st.status === 'abgelaufen' ? 'Abgelaufen (Einsatz beendet)' : 'Noch kein Link'

  return createPortal(
    <div className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/70 p-3 max-md:p-0" onClick={onSchliessen}>
      <div ref={panel} data-ui-modal="" role="dialog" aria-modal="true" aria-label="Freigabelink für Externe" tabIndex={-1}
        className="max-h-[94vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-dark-border bg-dark-bg-secondary outline-none max-md:h-[100dvh] max-md:max-h-none max-md:rounded-none" onClick={e => e.stopPropagation()}>
        {dialog}
        <div className="flex items-center justify-between border-b border-dark-border px-4 py-3">
          <span className="flex items-center gap-2 text-base font-semibold text-dark-text"><Share2 className="h-5 w-5" />Freigabelink für Externe</span>
          <button type="button" className="flex h-12 w-12 items-center justify-center rounded-lg text-dark-text-muted hover:text-dark-text" onClick={onSchliessen} aria-label="Schließen"><X className="h-5 w-5" /></button>
        </div>
        <div className="space-y-4 p-4">
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-100">
            Jeder mit diesem Link kann Livebild und Drohnenpositionen sehen, bis der Einsatz endet.
          </div>
          {fehler && <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">{fehler}</div>}

          {aktiv && url ? (
            <div className="grid gap-4 sm:grid-cols-[auto,1fr]">
              <div className="mx-auto w-56 rounded-lg bg-white p-2">
                {qr ? <img src={qr} alt="QR-Code zum Freigabelink" className="h-full w-full" /> : <div className="aspect-square" />}
              </div>
              <div className="min-w-0 space-y-2">
                <label className="text-xs text-dark-text-muted" htmlFor="freigabe-url">Link</label>
                <input id="freigabe-url" ref={feld} readOnly value={url} onFocus={e => e.currentTarget.select()}
                  className="w-full rounded-md border border-dark-border bg-dark-bg-tertiary px-2 py-2 text-xs text-dark-text" />
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" onClick={kopierenKlick}><Copy className="mr-1 h-4 w-4" />Link kopieren</Button>
                  <Button size="sm" variant="secondary" onClick={herunterladen}><Download className="mr-1 h-4 w-4" />QR herunterladen</Button>
                  <Button size="sm" variant="secondary" onClick={drucken}><Printer className="mr-1 h-4 w-4" />Drucken</Button>
                </div>
                {hinweis && <div className="text-xs text-green-300" role="status">{hinweis}</div>}
              </div>
            </div>
          ) : (
            <p className="text-sm text-dark-text-muted">
              {st?.status === 'keiner' && 'Für diesen Einsatz gibt es noch keinen Freigabelink.'}
              {st?.status === 'gesperrt' && 'Der Link wurde gesperrt und ist nicht mehr gültig.'}
              {st?.status === 'abgelaufen' && 'Der Einsatz ist beendet – der Link ist abgelaufen. Ein beendeter Einsatz bekommt keinen neuen Link; wurde er wieder geöffnet, kann ein neuer Link erzeugt werden.'}
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            {aktiv ? (
              <>
                <Button size="sm" variant="danger" disabled={laeuft} onClick={() => aktion('/sperren', { titel: 'Link sperren?', bestaetigenText: 'Sperren', hinweis: 'Externe, die den Link oder QR-Code haben, verlieren sofort den Zugriff auf Livebild und Drohnenpositionen. Der Link kann nicht wieder freigegeben werden; ein neuer Link lässt sich erzeugen.' })}><Ban className="mr-1 h-4 w-4" />Link sperren</Button>
                <Button size="sm" variant="secondary" disabled={laeuft} onClick={() => aktion('', { titel: 'Neuen Link erzeugen?', bestaetigenText: 'Neu erzeugen', hinweis: 'Der bisherige Link und QR-Code werden sofort ungültig. Wer sie bereits erhalten hat, braucht den neuen Link.' })}><RefreshCw className="mr-1 h-4 w-4" />Neuen Link erzeugen</Button>
              </>
            ) : (
              <Button size="sm" disabled={laeuft || !st || (st.einsatz_abgeschlossen)} onClick={() => aktion('')}>Link erzeugen</Button>
            )}
          </div>

          <div className="rounded-lg border border-dark-border bg-dark-bg-tertiary/50 p-3 text-sm">
            <div className="grid grid-cols-2 gap-x-4 gap-y-1">
              <span className="text-dark-text-muted">Status</span><span className="font-medium text-dark-text">{statusText}</span>
              {st?.link && <>
                <span className="text-dark-text-muted">Erzeugt</span><span className="text-dark-text">{zeit(st.link.erstellt_am)}{st.link.erstellt_von ? ` · ${st.link.erstellt_von}` : ''}</span>
                {st.link.gesperrt_am && <><span className="text-dark-text-muted">Gesperrt</span><span className="text-dark-text">{zeit(st.link.gesperrt_am)}</span></>}
                {st.link.abgelaufen_am && <><span className="text-dark-text-muted">Abgelaufen</span><span className="text-dark-text">{zeit(st.link.abgelaufen_am)}</span></>}
                <span className="text-dark-text-muted">Zuletzt geöffnet</span><span className="text-dark-text">{zeit(st.link.zuletzt_geoeffnet_am)}</span>
                <span className="text-dark-text-muted">Aufrufe</span><span className="text-dark-text">{st.link.aufrufe}</span>
              </>}
            </div>
            {!!st?.protokoll.length && (
              <div className="mt-2">
                <button type="button" className="min-h-[44px] text-xs text-primary-400 underline" onClick={() => setProtokollOffen(o => !o)}>
                  {protokollOffen ? 'Protokoll ausblenden' : `Protokoll anzeigen (${st.protokoll.length})`}
                </button>
                {protokollOffen && (
                  <ul className="mt-2 max-h-48 space-y-1 overflow-y-auto text-xs text-dark-text-muted">
                    {st.protokoll.map((p, i) => (
                      <li key={i}>{zeit(p.zeit)} · <span className="text-dark-text">{EREIGNIS[p.ereignis] || p.ereignis}</span>
                        {p.nutzer ? ` · ${p.nutzer}` : ''}{p.ip ? ` · IP ${p.ip}` : ''}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}
